import { useEffect, useMemo, useRef, useState } from 'react';
import { WireframeBackground } from '@/components/WireframeBackground';
import { Link } from 'react-router-dom';
import { getAllPosts } from './postRegistry';
import type { PostEntry } from './types';
import { ArrowLeft, ArrowRight, Pin, Search, X, type LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

function resolveIcon(name: string | undefined): LucideIcon | null {
  if (!name) return null;
  const icon = (LucideIcons as Record<string, unknown>)[name];
  if (icon) return icon as LucideIcon;
  return null;
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ── Category definitions ───────────────────────────────────────────────────

type Category = 'tutorial' | 'feature-guide' | 'reference' | 'editorial';

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'tutorial', label: 'Tutorial' },
  { value: 'feature-guide', label: 'Feature Guide' },
  { value: 'reference', label: 'Reference' },
  { value: 'editorial', label: 'Editorial' },
];

// ── Sidebar ────────────────────────────────────────────────────────────────

interface SidebarProps {
  query: string;
  onQueryChange: (q: string) => void;
  allTags: { tag: string; count: number }[];
  activeTags: Set<string>;
  disabledTags: Set<string>;
  onToggleTag: (tag: string) => void;
  onClear: () => void;
  totalPosts: number;
  visiblePosts: number;
  activeCategory: Category | null;
}

function Sidebar({
  query,
  onQueryChange,
  allTags,
  activeTags,
  disabledTags,
  onToggleTag,
  onClear,
  totalPosts,
  visiblePosts,
  activeCategory,
}: SidebarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isDirty =
    query.trim() !== '' || activeTags.size > 0 || activeCategory !== null;

  return (
    <aside className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Search */}
      <div className="flex-none">
        <label
          htmlFor="blog-search"
          className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-foreground"
        >
          Search
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={inputRef}
            id="blog-search"
            type="search"
            placeholder="Title or summary…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Tags */}
      <div className="flex min-h-0 flex-1 flex-col">
        <p className="mb-2 flex-none text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Filter by tag
        </p>
        <ul className="flex flex-col gap-1 overflow-y-auto">
          {allTags.map(({ tag, count }) => {
            const active = activeTags.has(tag);
            const disabled = !active && disabledTags.has(tag);
            return (
              <li key={tag}>
                <button
                  onClick={() => onToggleTag(tag)}
                  disabled={disabled}
                  className={[
                    'flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-xs transition-colors',
                    active
                      ? 'bg-primary/15 font-medium text-primary ring-1 ring-inset ring-primary/30'
                      : disabled
                        ? 'cursor-not-allowed opacity-35'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  ].join(' ')}
                >
                  <span>{tag}</span>
                  <span
                    className={[
                      'tabular-nums',
                      active ? 'text-primary/70' : 'text-muted-foreground/60',
                    ].join(' ')}
                  >
                    {count}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Footer: result count + clear */}
      <div className="flex-none flex items-center justify-between gap-2 border-t border-border pt-4">
        <span className="text-xs text-muted-foreground">
          {visiblePosts === totalPosts
            ? `${totalPosts} posts`
            : `${visiblePosts} of ${totalPosts}`}
        </span>
        {isDirty && (
          <button
            onClick={onClear}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>
    </aside>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ── Main component ─────────────────────────────────────────────────────────

export function BlogIndex() {
  const [posts, setPosts] = useState<PostEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    getAllPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [query, activeTags, activeCategory]);

  // Collect all tags sorted by frequency, auto-derived from loaded metadata
  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const { meta } of posts) {
      for (const tag of meta.tags ?? []) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag, count]) => ({ tag, count }));
  }, [posts]);

  // Filter posts by query, active category, and active tags; pinned posts float to the top
  const visiblePosts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = posts.filter(({ meta }) => {
      if (
        needle &&
        !meta.title.toLowerCase().includes(needle) &&
        !meta.summary.toLowerCase().includes(needle)
      ) {
        return false;
      }
      if (activeCategory && meta.category !== activeCategory) {
        return false;
      }
      if (activeTags.size > 0) {
        const postTags = new Set(meta.tags ?? []);
        for (const t of activeTags) {
          if (!postTags.has(t)) return false;
        }
      }
      return true;
    });
    // Stable sort: pinned first, then original date order within each group
    return filtered.sort((a, b) => {
      const aPin = a.meta.pinned ? 1 : 0;
      const bPin = b.meta.pinned ? 1 : 0;
      return bPin - aPin;
    });
  }, [posts, query, activeTags, activeCategory]);

  // Paginate the filtered list
  const totalPages = Math.ceil(visiblePosts.length / PAGE_SIZE);
  const pagedPosts = visiblePosts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Compute which inactive tags would yield zero results if toggled on.
  // A tag is "dead" when none of the currently visible posts carry it.
  const disabledTags = useMemo(() => {
    const visibleTagSets = visiblePosts.map(
      ({ meta }) => new Set(meta.tags ?? []),
    );
    const dead = new Set<string>();
    for (const { tag } of allTags) {
      if (activeTags.has(tag)) continue; // active tags are never disabled
      const wouldMatch = visibleTagSets.some((tags) => tags.has(tag));
      if (!wouldMatch) dead.add(tag);
    }
    return dead;
  }, [visiblePosts, allTags, activeTags]);

  function toggleTag(tag: string) {
    setActiveTags((prev) => {
      const next = new Set(prev);
      next.has(tag) ? next.delete(tag) : next.add(tag);
      return next;
    });
  }

  function clearFilters() {
    setQuery('');
    setActiveTags(new Set());
    setActiveCategory(null);
  }

  return (
    <div className="relative min-h-full bg-background px-4 py-12">
      <WireframeBackground />
      <div className="relative z-10 mx-auto max-w-6xl">
        <header className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Blog
          </h1>
          <p className="mt-2 text-muted-foreground">
            Tips, tutorials, and explorations in parametric 3D modelling with
            OpenSCAD.
          </p>
        </header>

        {/* ── Category filter bar ─────────────────────────────────── */}
        <div className="mb-8 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory(null)}
            className={[
              'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
              activeCategory === null
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
            ].join(' ')}
          >
            All
          </button>
          {CATEGORIES.map(({ value, label }) => (
            <button
              key={value}
              onClick={() =>
                setActiveCategory(activeCategory === value ? null : value)
              }
              className={[
                'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
                activeCategory === value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          /* Skeleton */
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_220px]">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-lg border border-border bg-muted/30"
                />
              ))}
            </div>
            <div className="hidden xl:block">
              <div className="space-y-3">
                <div className="h-8 animate-pulse rounded bg-muted/30" />
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-7 animate-pulse rounded bg-muted/30"
                  />
                ))}
              </div>
            </div>
          </div>
        ) : posts.length === 0 ? (
          <p className="text-muted-foreground">No posts published yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_220px]">
            {/* ── Post list ─────────────────────────────────────────── */}
            <div>
              {visiblePosts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border py-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    No posts match your filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="mt-2 text-sm text-primary hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <ul className="space-y-4">
                    {pagedPosts.map(({ meta }) => (
                      <li key={meta.slug}>
                        <Link
                          to={`/blog/${meta.slug}`}
                          className={[
                            'group block rounded-lg border p-6 transition-colors',
                            meta.pinned
                              ? 'border-primary/30 bg-primary/5 hover:border-primary/60 hover:bg-primary/10'
                              : 'border-border bg-card hover:border-primary/50 hover:bg-card/80',
                          ].join(' ')}
                        >
                          {meta.pinned && (
                            <div className="mb-3 flex items-center gap-1.5">
                              <Pin className="h-3 w-3 fill-primary text-primary" />
                              <span className="text-xs font-medium uppercase tracking-widest text-primary/80">
                                Pinned
                              </span>
                            </div>
                          )}
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground group-hover:text-primary">
                                {(() => {
                                  const Icon = resolveIcon(meta.icon);
                                  return Icon ? (
                                    <Icon className="h-4 w-4 shrink-0 text-foreground" />
                                  ) : null;
                                })()}
                                {meta.title}
                              </h2>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {meta.summary}
                              </p>
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <time
                                  dateTime={meta.date}
                                  className="text-xs text-muted-foreground"
                                >
                                  {formatDate(meta.date)}
                                </time>
                                {meta.readingTime != null && (
                                  <>
                                    <span
                                      className="text-xs text-muted-foreground"
                                      aria-hidden
                                    >
                                      ·
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {meta.readingTime} min read
                                    </span>
                                  </>
                                )}
                                {meta.tags && meta.tags.length > 0 && (
                                  <>
                                    <span
                                      className="text-xs text-muted-foreground"
                                      aria-hidden
                                    >
                                      ·
                                    </span>
                                    {meta.tags.map((tag) => (
                                      <span
                                        key={tag}
                                        className={[
                                          'rounded-full border px-2 py-0.5 text-xs transition-colors',
                                          activeTags.has(tag)
                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                            : 'border-border bg-muted text-muted-foreground',
                                        ].join(' ')}
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                            <ArrowRight className="mt-1 h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-between gap-4">
                      <button
                        onClick={() => setPage((p) => p - 1)}
                        disabled={page === 1}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Previous
                      </button>
                      <span className="text-xs text-muted-foreground">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page === totalPages}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                      >
                        Next
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Sidebar ───────────────────────────────────────────── */}
            <div className="order-first xl:order-last xl:flex xl:flex-col">
              <div className="xl:sticky xl:top-8 xl:flex xl:max-h-[calc(100dvh-4rem)] xl:flex-col">
                <Sidebar
                  query={query}
                  onQueryChange={setQuery}
                  allTags={allTags}
                  activeTags={activeTags}
                  disabledTags={disabledTags}
                  onToggleTag={toggleTag}
                  onClear={clearFilters}
                  totalPosts={posts.length}
                  visiblePosts={visiblePosts.length}
                  activeCategory={activeCategory}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
